import React, { useEffect, useMemo, useState } from 'react';
import { Select, SelectItem, Selection, Input } from '@nextui-org/react';
import { MarketData, SettlementDetails } from 'common/src/utils/types';
import { useTranslation } from '../../hooks/useTranslation';

interface FormatFilterProps {
  data: SettlementDetails[] | MarketData[];
  selectedFormat: string | string[];
  onSelectionChange: (value: string[]) => void;
  selectionMode?: 'single' | 'multiple';
  label?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  defaultSelectedKeys?: string[];
  defaultSelectAll?: boolean;
  hideAllOption?: boolean;
}

const FormatFilter: React.FC<FormatFilterProps> = ({
  data,
  selectedFormat,
  onSelectionChange,
  selectionMode = 'single',
  label,
  className = 'min-w-32',
  size = 'sm',
  defaultSelectedKeys,
  defaultSelectAll = false,
  hideAllOption = false,
}) => {
  const { t } = useTranslation('common');
  const finalLabel = label || t('Formato');
  const [search, setSearch] = useState('');
  const formats = useMemo(() => {
    // Create a map to count records per format
    const formatCount = data.reduce((acc, item) => {
      // Handle both SettlementDetails (format) and MarketData (weight_kg)
      const format = 'format' in item ? item.format : item.weight_kg;
      if (format) {
        acc[format] = (acc[format] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    // Convert to array and sort by count
    return Object.entries(formatCount)
      .sort(([, countA], [, countB]) => countB - countA)
      .map(([format]) => format);
  }, [data]);

  const filteredFormats = useMemo(() => {
    if (!search) return formats;
    const lower = search.toLowerCase();
    return formats.filter((f) => String(f).toLowerCase().includes(lower));
  }, [formats, search]);

  useEffect(() => {
    if (
      selectionMode === 'multiple' &&
      defaultSelectAll &&
      formats.length > 0
    ) {
      onSelectionChange(formats);
    }
  }, []);

  if (formats.length === 0) {
    return null;
  }

  const handleSelectionChange = (keys: Selection) => {
    if (selectionMode === 'multiple') {
      const keysArray = Array.from(keys);

      if (keysArray.includes('all')) {
        // If 'all' is selected and there were previous selections, clear all
        if (Array.isArray(selectedFormat) && selectedFormat.length > 0) {
          onSelectionChange([]);
        } else {
          // Otherwise, select all formats
          onSelectionChange(formats);
        }
      } else {
        // Normal selection behavior for individual items
        onSelectionChange(keysArray as string[]);
      }
    } else {
      // Keep existing behavior for single selection
      const selectedKeys = Array.from(keys);
      onSelectionChange(selectedKeys as string[]);
    }
  };

  return (
    <Select
      label={finalLabel}
      selectedKeys={
        selectionMode === 'multiple'
          ? Array.isArray(selectedFormat)
            ? new Set(selectedFormat)
            : new Set([])
          : new Set([selectedFormat as string])
      }
      onSelectionChange={handleSelectionChange}
      selectionMode={selectionMode}
      defaultSelectedKeys={defaultSelectedKeys && new Set(defaultSelectedKeys)}
      className={className}
      size={size}
      aria-label={`${t('Selecciona formato')}`}
      renderValue={(items) => {
        if (
          selectionMode === 'multiple' &&
          Array.isArray(selectedFormat) &&
          selectedFormat.length === formats.length
        ) {
          return (
            <div className='flex items-center gap-2'>{t('Todos los formatos')}</div>
          );
        }
        if (Array.isArray(selectedFormat) && selectedFormat.length > 0) {
          return (
            <div className='flex items-center gap-2'>
              {selectedFormat.join(', ')}
            </div>
          );
        }
        return items;
      }}
      listboxProps={{
        topContent: (
          <div className='sticky top-0 z-10 bg-content1 px-1 pt-1 pb-1'>
            <Input
              size='sm'
              placeholder={t('Buscar formato...')}
              value={search}
              onValueChange={setSearch}
              isClearable
              onClear={() => setSearch('')}
            />
          </div>
        ),
      }}
    >
      {selectionMode === 'single' && !hideAllOption && (
        <SelectItem key='all' value='all' textValue={t('Todos')}>
          {t('Todos')}
        </SelectItem>
      )}
      {selectionMode === 'multiple' && !hideAllOption && (
        <SelectItem key='all' value='all' textValue={t('Todos')}>
          <div className='flex justify-between items-center'>
            <span>
              {Array.isArray(selectedFormat) && selectedFormat.length > 0
                ? t('Deseleccionar todos')
                : t('Seleccionar todos')}
            </span>
          </div>
        </SelectItem>
      )}
      {filteredFormats.map((format) => (
        <SelectItem key={format} value={format} textValue={format}>
          {format}
        </SelectItem>
      ))}
    </Select>
  );
};

export default React.memo(FormatFilter);
